import React from "react";
import { DefaultButton, Dropdown, PrimaryButton, Stack, ITag, IBasePickerSuggestionsProps, Label, TagPicker, Text, IDropdownOption, DropdownMenuItemType, ISelectableOption } from "@fluentui/react";
import { Formik } from "formik";
import {
  addTempAndRhPage, updateTempAndRhPage, getSensorsByFloorOrQuadrant, getTempRHPageDetails
} from "../../../services/customers/CustomerService";
import { IDataCenter, IPowerReportConfigurations, ITempAndRhPageConfiguration } from "../../../vm";
import { childrenGap } from "../../common/Constants";
import CustomDrawer from "../../common/CustomDrawer";
import { CustomTextField } from "../../common/Library";
import { ToastContext } from "../../common/ToastProvider";
import * as yup from "yup";
import { searchQuadrantsOfDC } from "../../../services/datacenter/DatacenterService";
import { Loading } from "../../../services/Loading";

const pageSchema = yup.object().shape({
  name: yup.string().required("required"),
  floorId: yup.string().required("required"),
  quadrantId: yup.string().optional(),
  sequence: yup.number().required("required"),
  sensorIds: yup.array().min(1, "Select at least one").max(6, "Can't select more than six"),
});

export interface ManageTempRhPageDialogProps {
  tempRhPageId?: string;
  handleDialogClose: any;
  customerId: string;
  datacenterId: string;
  sequence: number;
  datacenter: IDataCenter;
  reportConfig: IPowerReportConfigurations
}
var groupBy = "quadrant-wise";
const ManageTempRhPageDialog: React.FC<ManageTempRhPageDialogProps> = ({
  tempRhPageId,
  handleDialogClose,
  customerId,
  datacenterId,
  sequence,
  datacenter,
  reportConfig
}) => {
  //state
  const [isLoading, setLoading] = React.useState(false as boolean);
  const [tempRhPage, setTempRhPage] = React.useState({
    name: "",
    floorId: "",
    quadrantId: "",
    datacenterId: datacenterId,
    customerId: customerId,
    sequence: sequence,
    sensorIds: [],
  } as ITempAndRhPageConfiguration);
  const [quadrantDict, setQuadrantDict] = React.useState({} as any);
  const [sensors, setSensors] = React.useState([] as ITag[]);
  const [allSensorsDict, setAllSensorsDict] = React.useState({} as any);
  const [floorOptions, setFloorOptions] = React.useState([] as IDropdownOption[]);

  // custom hooks
  const { showToast } = React.useContext(ToastContext);

  // hooks
  React.useEffect(() => {
    const asyncFunc = async () => {
        // prepare floor options with building
        let buildingNameDict: any = {};
        if (datacenter?.buildings?.length > 0) {
            buildingNameDict = datacenter.buildings.reduce((acc: any, d) => {
              acc[d._id] = d.name;
              return acc;
            }, {})
        }
        const floorOptions: IDropdownOption[] = [];
        if (datacenter?.floors?.length > 0) {
          let tempB: any;
          datacenter.floors.forEach((d) => {
            if (tempB == null || tempB !== d.buildingId) {
              if (tempB != null) {
                floorOptions.push({
                  key: "divider_1",
                  text: "-",
                  itemType: DropdownMenuItemType.Divider,
                });
              }
              floorOptions.push({
                key: d.buildingId,
                text: buildingNameDict[d.buildingId] || "",
                itemType: DropdownMenuItemType.Header,
              });
              tempB = d.buildingId;
            }
            floorOptions.push({
              key: d._id,
              text: d.name,
              data: { buildingName: buildingNameDict[d.buildingId] || "" },
            });
          })
        }
        setFloorOptions(floorOptions);
        groupBy = reportConfig?.groupByTempRh === "floor-wise" ? "floor-wise" : "quadrant-wise";
        setLoading(true);
        if (tempRhPageId) {
            await getTempRhPageDetails(tempRhPageId);
        }
        if (groupBy === "quadrant-wise" || (tempRhPage.quadrantId)) {
            await getQuadrants();
        }
        setLoading(false);
    }
    asyncFunc();
  }, []);

  const getTempRhPageDetails = async (id: string) => {
    const result = await getTempRHPageDetails({ tempRhPageId: id });
    if (result?.success) {
        setTempRhPage(result.data);
        let dict = {}
        if (result.data.sensors && result.data.sensors.length > 0) {
            dict = result.data.sensors.reduce((acc: any, d: any) => {
                acc[d._id] = d;
                return acc;
            }, {})
            setAllSensorsDict(dict);
        }
        await getSensors(result.data.floorId, result.data.quadrantId, dict);
    } else {
     showToast(result?.message || "Error while getting page details", "error");
    }
  }

  const getQuadrants = async () => {
    const result = await searchQuadrantsOfDC(datacenterId);
    if (result?.success) {
        const quadrantDictByFloor = result.data.reduce((acc: any, d) => {
            if (acc[d.floorId] == null) acc[d.floorId] = [];
            acc[d.floorId].push(d);
            return acc;
        }, [])
        setQuadrantDict(quadrantDictByFloor);
    } else {
       showToast(result?.message || "Error while getting quadrants", "error");
    }
  }

  const closeAddDialog = (data?: ITempAndRhPageConfiguration) => {
    handleDialogClose(data);
  };

  const getQuadrantListByFloor = (floorId: string) => {
    const quadrants = Object.keys(quadrantDict).length > 0 && quadrantDict[floorId] && quadrantDict[floorId].length > 0 ? quadrantDict[floorId] : [];
    return quadrants.map((d: any) => ({ key: d._id as string, text: d.quadrant }))
  };

  const getSensors = async (floorId: string, quadrantId?: string, sensorDict?: any) => {
    let searchObj: any = {
        customerId: customerId,
        datacenterId: datacenterId,
        floorId: floorId,
        isFilter: true
    }
    if (quadrantId) {
        searchObj["quadrantId"] = quadrantId
    }
    const result = await getSensorsByFloorOrQuadrant(searchObj);
    if (result?.success) {
        const sensors = result.data.map((d: any) => ({ key: d._id as string, name: d.name }))
        setSensors(sensors);
        const dict = result.data.reduce((acc: any, d: any) => {
            acc[d._id] = d;
            return acc
        }, {});
        let sDict = { ...allSensorsDict };
        if (sensorDict) {
          sDict = { ...sDict, ...sensorDict };
        }
        setAllSensorsDict({ ...sDict, ...dict });
    } else {
      showToast(result?.message || "Error while getting sensors", "error");
    }
  };

  const pickerSuggestionsProps: IBasePickerSuggestionsProps = {
    noResultsFoundText: "No sensors found",
  };

  const filterSuggestedTags = (filterText: string, tagList: ITag[]): ITag[] => {
    let sensorList = [...sensors];

    return filterText
      ? sensorList.filter(
          (tag) =>
            tag.name.toLowerCase().indexOf(filterText.toLowerCase()) === 0 &&
            !listContainsTagList(tag, tagList)
        )
      : [];
  };

  const showAllTags = (selectedItems?: ITag[] | undefined) => {
    let sensorList = [...sensors];
    return removeDuplicates(sensorList, selectedItems);
  };

  function removeDuplicates(tagList: ITag[], tags?: ITag[]) {
    return tagList.filter((tag) => !listContainsTagList(tag, tags));
  }

  const listContainsTagList = (tag: ITag, tagList?: ITag[]) => {
    if (!tagList || !tagList.length || tagList.length === 0) {
      return false;
    }
    return tagList.some((compareTag) => compareTag.key === tag.key);
  };

  const onRenderOption = (option?: ISelectableOption): JSX.Element => {
    if (!option) return <div />;
    return (
      <div className="d-flex">
        {/* {!option.itemType && <Icon path={mdiFloorPlan} size={0.8} />} */}
        <span style={{ marginLeft: option.itemType ? "0px" : "12px" }}>
          {option.text}
        </span>
      </div>
    );
  };

  const onRenderTitle = (options?: IDropdownOption[]): JSX.Element => {
    if (!options) return <div />;
    const option = options[0];

    return (
      <div>
        <span>{option.data.buildingName + "-" + option.text}</span>
      </div>
    );
  };

  return (
    <React.Fragment>
        {isLoading && <Loading />}
      <CustomDrawer
        title={
          tempRhPage._id
            ? "Update Temp/RH Page"
            : "Add Temp/RH Page"
        }
        handelOpen={true}
        onClose={() => closeAddDialog()}
      >
        <Stack>
          <Formik
            enableReinitialize
            initialValues={tempRhPage}
            validationSchema={pageSchema}
            onSubmit={async (
              values: ITempAndRhPageConfiguration,
              { setSubmitting }
            ) => {
              let obj = { ...values };
              let result;
              setLoading(true);
              if (tempRhPage._id) {
                result = await updateTempAndRhPage(obj);
              } else {
                result = await addTempAndRhPage(obj);
              }
              setLoading(false);
              if (result.success) {
                showToast(`Temp Rh Page ${tempRhPage._id ? "updated" : "added"} successfully`, "success");
                closeAddDialog(result.data);
              } else {
                showToast(
                  result?.message ||
                    `Error while ${
                      tempRhPage._id ? "updating" : "adding"
                    } temp/rh page`,
                  "error"
                );
              }
              setSubmitting(false);
            }}
          >
            {({ handleSubmit, setFieldValue, values, errors, touched }) => (
              <form onSubmit={handleSubmit}>
                <Stack wrap tokens={childrenGap}>
                  <Stack.Item>
                    <CustomTextField name={"name"} label={"Name"} />
                  </Stack.Item>
                  <Stack.Item>
                    <CustomTextField name={"sequence"} label={"Sequence"} type="number" />
                  </Stack.Item>
                  <Stack.Item className="custom-dropdown mb-30">
                    <Dropdown
                      options={floorOptions}
                      // options={datacenter?.floors?.map((d) => ({
                      //   key: d._id as string,
                      //   text: d.name as string
                      // }))}
                      onRenderOption={onRenderOption}
                      onRenderTitle={onRenderTitle}
                      selectedKey={values.floorId}
                      onChange={async (e, item) => {
                        setFieldValue("floorId", item?.key);
                        setFieldValue("sensorIds", []);
                        if(groupBy === "floor-wise") {
                            await getSensors(item?.key as string)
                        }
                      }}
                      label="Floor"
                      placeholder="Select an option"
                      errorMessage={
                        (errors.floorId &&
                          touched.floorId &&
                          errors.floorId) as string
                      }
                    />
                  </Stack.Item>
                  {(values.quadrantId || groupBy === "quadrant-wise") && (
                   <Stack.Item className="custom-dropdown mb-30">
                    <Dropdown
                      options={values.floorId ? getQuadrantListByFloor(values.floorId as string): []}
                      selectedKey={values.quadrantId}
                      onChange={async (e, item) => {
                        setFieldValue("quadrantId", item?.key);
                        setFieldValue("sensorIds", []);
                        await getSensors(values.floorId, item?.key as string)
                      }}
                      label="Quadrant"
                      placeholder="Select an option"
                      errorMessage={
                        (errors.quadrantId &&
                          touched.quadrantId &&
                          errors.quadrantId) as string
                      }
                    />
                  </Stack.Item>
                  )}
                  <Stack.Item className="custom-picker">
                      <Label>Sensors</Label>
                      <TagPicker
                        removeButtonAriaLabel="Remove"
                        selectedItems={values.sensorIds.map((d) => {
                            const found = allSensorsDict[d]
                            if (found) {
                              return { key: found._id, name: found.name };
                            } else {
                              return { key : "", name: "" };
                            }
                        })}
                        onResolveSuggestions={filterSuggestedTags as any}
                        pickerSuggestionsProps={pickerSuggestionsProps}
                        onEmptyResolveSuggestions={showAllTags}
                        inputProps={{
                          placeholder: "Search a particular sensor",
                        }}
                        onChange={(items: any) => {
                          let sensorIds: string[] = [];
                          items.forEach((i: any) => {
                            sensorIds.push(i.key);
                          });
                          setFieldValue("sensorIds", sensorIds);
                        }}
                      />
                      {errors.sensorIds && touched.sensorIds && (
                        <Text className="error-msg">
                          {errors.sensorIds}
                        </Text>
                      )}
                    </Stack.Item>
                  <Stack.Item>
                    <Stack
                      horizontal
                      wrap
                      tokens={childrenGap}
                      horizontalAlign="end"
                    //   className="pt-16"
                    >
                      <DefaultButton
                        text="Cancel"
                        onClick={() => closeAddDialog()}
                        className="borderless-btn"
                      />
                      <PrimaryButton
                        type="submit"
                        text={tempRhPage._id ? "Update" : "Add"}
                      />
                    </Stack>
                  </Stack.Item>
                </Stack>
              </form>
            )}
          </Formik>
        </Stack>
      </CustomDrawer>
    </React.Fragment>
  );
};

export default ManageTempRhPageDialog;
